/**
 * Task hierarchy utilities for sorting tasks into depth-first parent-child order.
 *
 * @see PlanTasksTimelineView - primary consumer for timeline (Gantt) rendering
 */ // Models
/**
 * Sorts tasks into depth-first hierarchical order where each parent appears immediately
 * before its children. Tasks whose parent is not in the input list (orphans) are appended
 * at depth 0.
 *
 * @param tasks The flat list of tasks to sort.
 * @returns Sorted tasks and a depth map.
 */ export function sortTasksInHierarchicalOrder(tasks) {
    // Build adjacency list: parentId → children (preserves input order for siblings)
    const childrenOf = new Map();
    for (const task of tasks){
        const parentId = task.parentTaskId ?? null;
        let siblings = childrenOf.get(parentId);
        if (siblings == null) {
            siblings = [];
            childrenOf.set(parentId, siblings);
        }
        siblings.push(task);
    }
    const sortedTasks = [];
    const depthMap = new Map();
    // Depth-first walk starting from root tasks (parentTaskId === null).
    // A visited set prevents infinite recursion if the data contains cycles
    // (e.g., A.parentTaskId = B and B.parentTaskId = A, or self-parent).
    const walk = (parentId, depth, visited)=>{
        if (parentId && visited.has(parentId)) {
            return;
        }
        visited.add(parentId);
        const children = childrenOf.get(parentId) ?? [];
        for (const task of children){
            depthMap.set(task.id, depth);
            sortedTasks.push(task);
            walk(task.id, depth + 1, visited);
        }
    };
    walk(null, 0, new Set());
    // Append any tasks not reachable from root (orphaned tasks whose parent is not in the list)
    if (sortedTasks.length < tasks.length) {
        const seen = new Set(sortedTasks.map((t)=>t.id));
        for (const task of tasks){
            if (!seen.has(task.id)) {
                depthMap.set(task.id, 0);
                sortedTasks.push(task);
            }
        }
    }
    return {
        sortedTasks,
        depthMap
    };
}
